import i18next from "i18next";
import L from "leaflet";
import React, { useEffect, useMemo, useState } from "react";
import { MapContainer, Marker, TileLayer, useMap, useMapEvents } from "react-leaflet";
import { useDispatch, useSelector } from "react-redux";
import {
	setLocationModalInfoVisible,
	getSelectedLocation,
	getWatchLocation,
	setSelectedLocation,
	getLocationModalInfoVisible,
} from "../../state/map/mapSlice";
import SimpleDialog, { DialogPosition } from "../SimpleDialog";
import CoordinateInfo from "./CoordinateInfo";
import { LocationsResults } from "../../utils/types";
import { isLogged } from "../../state/auth/authSlice";

const INITIAL_ZOOM = 5;

const darkenHexColor = (hex: string, percent: number): string => {
	if (hex.startsWith("#")) {
		hex = hex.slice(1);
	}

	const r = parseInt(hex.slice(0, 2), 16);
	const g = parseInt(hex.slice(2, 4), 16);
	const b = parseInt(hex.slice(4, 6), 16);

	const darken = (color: number) => Math.max(0, Math.min(255, Math.floor(color * (1 - percent / 100))));

	const rDark = darken(r);
	const gDark = darken(g);
	const bDark = darken(b);

	const toHex = (color: number) => color.toString(16).padStart(2, "0");

	return `#${toHex(rDark)}${toHex(gDark)}${toHex(bDark)}`;
};

function Events({ setMapFly }) {
	const map = useMap();

	useEffect(() => {
		setMapFly(map);
	}, [map, setMapFly]);

	return null;
}

function MapClickHandler() {
	const dispatch = useDispatch();

	useMapEvents({
		click(e: any) {
			dispatch(setSelectedLocation({ lat: e.latlng.lat, lng: e.latlng.lng }));
		},
	});

	return null;
}

interface Props {
	locations?: LocationsResults | undefined;
	timelineDisplayed?: boolean;
}

export default function Map({ locations, timelineDisplayed }: Props) {
	const watchLocation = useSelector(getWatchLocation);
	const modalInfoVisible = useSelector(getLocationModalInfoVisible);
	const logged = useSelector(isLogged);

	const dispatch = useDispatch();

	const [sample, setSample] = useState<any>();
	const [mapFly, setMapFly] = useState<any>();
	const selectedLocation = useSelector(getSelectedLocation);

	const mapRef = React.useRef<any>(null);

	useEffect(() => {
		if (mapRef.current && selectedLocation?.lat && selectedLocation?.lng) {
			mapRef.current.flyTo([selectedLocation.lat, selectedLocation.lng], mapRef.current.getZoom(), {
				duration: 0.5,
			});

			setTimeout(() => {
				mapRef.current.panBy([0, -130]);
			}, 600);
		}
	}, [selectedLocation]);

	useEffect(() => {
		if (!modalInfoVisible) {
			setSample(null);
		}
	}, [modalInfoVisible]);

	const createIcon = (location: any) => {
		const size = location.size;

		var color = { backgroundColor: "", borderColor: "" };
		if (sample) {
			if (location.id == sample.id) {
				color = {
					backgroundColor: location.color,
					borderColor: darkenHexColor(location.color, 40),
				};
			} else {
				color = { backgroundColor: "#e6e6e6", borderColor: "#bab8b8" };
			}
		} else {
			color = {
				backgroundColor: location.color,
				borderColor: darkenHexColor(location.color, 40),
			};
		}

		return L.divIcon({
			iconAnchor: location.iconAnchor,
			className: "",
			html: `<div data-testid="map-marker" style="background-color: ${color.backgroundColor}; border: 1px solid ${color.borderColor} ;width: ${size}px; height: ${size}px; border-radius: ${size}px"/>`,
		});
	};

	const markers = useMemo(
		() =>
			locations?.locations.map((location, index) => (
				<Marker
					key={location.id ?? index}
					data-testid="marker"
					eventHandlers={{
						click: () => {
							dispatch(setLocationModalInfoVisible(true));
							setSample(location);
							mapFly.flyTo([location.lat, location.lng], mapFly.getZoom(), {
								duration: 0.5,
							});
						},
					}}
					icon={createIcon(location)}
					position={[location.lat, location.lng]}
				/>
			)),
		[locations, setSample, sample, mapFly]
	);

	return (
		<div data-testid="map-container" className="position-relative">
			<>
				{locations && (
					<div
						style={{ left: 100, marginBottom: "1.4rem" }}
						className="position-absolute bottom-0 rounded-1 p-1 px-2 bg-white z-2"
					>
						<div>{i18next.t("results") + ": " + locations.total}</div>
					</div>
				)}

				{sample && modalInfoVisible && (
					<SimpleDialog
						style={{ maxWidth: "20vw" }}
						position={DialogPosition.Left}
						open={true}
						onClose={() => setSample(null)}
					>
						<CoordinateInfo
							grouped={locations?.grouped}
							onClose={() => setSample(null)}
							sample={sample}
						/>
					</SimpleDialog>
				)}
			</>

			<MapContainer
				ref={mapRef}
				style={{ height: `calc(100vh - ${timelineDisplayed ? 84 : 0}px)`, zIndex: 1 }}
				center={[-30.90328, -56.18816]}
				zoom={INITIAL_ZOOM}
				scrollWheelZoom={true}
				className="markercluster-map"
				id="map"
				key={timelineDisplayed ? "0" : "1"}
			>
				<TileLayer
					attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
					url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager_labels_under/{z}/{x}/{y}{r}.png"
				/>

				<>
					{markers}

					{watchLocation && <MapClickHandler />}
					{selectedLocation && !!selectedLocation.lat && !!selectedLocation.lng && (
						<div data-testid="map-marker">
							<Marker position={selectedLocation} />
						</div>
					)}
					<Events setMapFly={setMapFly} />
				</>
			</MapContainer>
		</div>
	);
}
